import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateLostReasonRequestSchema,
  type CreateLostReasonRequest,
  type LostReasonDetail,
  type LostReasonListItem,
  UpdateLostReasonRequestSchema,
  type UpdateLostReasonRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateLostReason, useUpdateLostReason } from '../use-lost-reasons';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';

interface LostReasonFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  lostReason?: LostReasonDetail | LostReasonListItem | null;
}

const CREATE_DEFAULTS: CreateLostReasonRequest = {
  code: '',
  name: '',
  description: '',
  requiresBudget: false,
  isAutoOnly: false,
  sortOrder: 0,
  isActive: true,
};

/**
 * Field set shared by create + edit. Reads the form via context
 * (UzFormField → useFormContext), so it renders inside either UzForm.
 *
 * 2026-07-20. Code is intentionally NOT rendered - it's auto-derived
 * from the name via `useAutoDeriveCode` on the parent, and the server
 * appends `_2` / `_3` / ... on any unique-constraint collision so the
 * operator never has to think about it.
 */
const LostReasonFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Budget"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="requiresBudget">
      {({ field }) => (
        <UzSwitch
          label="Requires budget"
          description="When on, a lead cannot be marked Lost with this reason unless it has a budget (the agent is asked to add one)."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isAutoOnly">
      {({ field }) => (
        <UzSwitch
          label="Automatic only"
          description="When on, only the system (auto-close cron) can apply this reason. Agents can't pick it in the Lost / follow-up dropdowns."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive reasons are hidden from lead forms."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const LostReasonFormDrawer = ({
  open,
  onOpenChange,
  mode,
  lostReason,
}: LostReasonFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createLostReason = useCreateLostReason();
  const updateLostReason = useUpdateLostReason();
  const pending = createLostReason.isPending || updateLostReason.isPending;

  const createForm = useForm<CreateLostReasonRequest>({
    resolver: zodResolver(CreateLostReasonRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateLostReasonRequest>({
    resolver: zodResolver(UpdateLostReasonRequestSchema),
    defaultValues: {},
  });

  // Code is hidden and driven entirely by `useAutoDeriveCode` on
  // create - the operator only types the name. On create-time
  // uniqueness collisions the server appends `_2` / `_3` / ... and
  // retries (see LostReasonsService.create), so `deriveCode(name)`
  // is just the STARTING point, never a hard requirement.
  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (lostReason) {
      // `code` is intentionally omitted from the edit reset - it's
      // the stable machine identifier, read-only post-create. The
      // update payload never includes it (schema is `.partial()`).
      updateForm.reset({
        name: lostReason.name,
        description: lostReason.description ?? '',
        requiresBudget: lostReason.requiresBudget,
        isAutoOnly: lostReason.isAutoOnly,
        sortOrder: lostReason.sortOrder,
        isActive: lostReason.isActive,
      });
    }
  }, [open, isCreate, lostReason, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field).
    await createLostReason.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!lostReason) return;
    await updateLostReason.mutateAsync({
      id: lostReason.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <LostReasonFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add lost reason' : `Edit ${lostReason?.name ?? 'lost reason'}`}
      description="The name is what agents see."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
